// LoadFilter.tsx
import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { datePickerPopperProps } from "components/admin/datePickerPopper";
import { useClickOutside } from "../../../hooks/useClickOutside";

interface LoadFilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    fromDate?: string;
    toDate?: string;
  }) => void;
  onClear: () => void;
  initialFromDate?: string;
  initialToDate?: string;
  dateRangeLabel?: string;
}

const LoadFilter: React.FC<LoadFilterProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  initialFromDate,
  initialToDate,
  dateRangeLabel = "Requested Date Range",
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Detect clicks outside the filter panel (only while open). The date pickers
  // render into a body-level portal (see datePickerPopperProps), so clicks
  // inside that portal must not count as "outside".
  useClickOutside(panelRef, onClose, isOpen, "mousedown", "#admin-datepicker-portal");

  React.useEffect(() => {
    if (isOpen) {
      if (initialFromDate) {
        const [y, m, d] = initialFromDate.split('-').map(Number);
        setFromDate(new Date(y, m - 1, d));
      } else {
        setFromDate(null);
      }
      if (initialToDate) {
        const [y, m, d] = initialToDate.split('-').map(Number);
        setToDate(new Date(y, m - 1, d));
      } else {
        setToDate(null);
      }
    }
  }, [isOpen, initialFromDate, initialToDate]);

  // Lock the app shell's scroll container while the panel is open. The panel
  // itself is fixed/absolute (it doesn't scroll with the page), but the
  // table behind it lives inside <main> (see MainLayout — "the ONLY scroll
  // container"), which keeps scrolling under a fixed-position panel on
  // mobile unless we stop it here too.
  React.useEffect(() => {
    if (!isOpen) return;
    const mainEl = document.querySelector('main');
    if (!mainEl) return;
    const previousOverflow = mainEl.style.overflow;
    mainEl.style.overflow = 'hidden';
    return () => {
      mainEl.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Requested dates can never be in the future, so cap selection at today.
  const today = new Date();

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleApply = () => {
    onApply({
      fromDate: fromDate ? formatDateLocal(fromDate) : undefined,
      toDate: toDate ? formatDateLocal(toDate) : undefined,
    });
    onClose();
  };

  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    onClear();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — mobile only, dims background content and closes on outside tap.
          z-20 (above the table's sticky header, which is z-10) so the header
          doesn't visually poke through the dimmed background. */}
      <div
        className="fixed inset-0 z-20 bg-black bg-opacity-40 sm:hidden"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-30 w-auto max-w-[calc(100vw-2rem)] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:translate-y-0 sm:mt-2 sm:w-96 sm:max-w-none bg-white rounded-lg shadow-lg"
      >
      <div className="flex items-center justify-end p-3 border-b border-lightgray-100">
        <button onClick={onClose}>
          <svg className="w-3 h-3" viewBox="0 0 14 14">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
          </svg>
        </button>
      </div>
      <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
        <p className="text-sm">{dateRangeLabel}</p>
        <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
          <DatePicker
            selected={fromDate}
            onChange={handleFromDateChange}
            selectsStart
            startDate={fromDate}
            endDate={toDate}
            maxDate={toDate || today}
            placeholderText="From Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperClassName={`${datePickerPopperProps.popperClassName} st-loadfilter-datepicker`}
          />
          <DatePicker
            selected={toDate}
            onChange={handleToDateChange}
            selectsEnd
            startDate={fromDate}
            endDate={toDate}
            minDate={fromDate || undefined}
            maxDate={today}
            placeholderText="To Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperClassName={`${datePickerPopperProps.popperClassName} st-loadfilter-datepicker`}
          />
        </div>
      </div>
      <div className="flex flex-wrap justify-between gap-2 p-3">
        <button className="px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md text-lightgray-200" onClick={onClose}>
          Cancel
        </button>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-3 py-2 border border-[#FFBC00] rounded-md text-[#FFBC00] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClear}
            disabled={!fromDate && !toDate}
          >
            Clear All
          </button>
          <button className="px-3 py-2 bg-[#FFBC00] rounded-md text-[#F5F9FE]" onClick={handleApply}>
            Apply
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoadFilter;

